import { isNil } from 'lodash';
import { Component, useCallback, useContext } from 'react';
import { findDOMNode } from 'react-dom';
import { useEntitySelection } from '@shared/api/providers/entitySelection';
import { CardChannelContext } from '@shared/components/blocks/contexts/channel';
import { useToast } from '@shared/components/toast';
import { Entity, toCreateWorkflowEntity } from '@shared/data/entity/Entity';
import { useDuplicateEntities } from '@shared/scenes/cards/entityBulkActions/hooks';
import { useDeleteEntitiesMutation } from '@shared/scenes/cards/entityBulkActions/useDeleteWorkflowEntities';
import { MBActionSheet } from '@shared/util/actionSheet';
import { i18n } from '@shared/util/i18n';
import { useNavigator } from '@shared/util/navigation';
import { ChannelPermissionsContext } from '@shared/util/permissions/ChannelPermissionsContext';

interface OverflowMenuHooks {
  showOverflowMenu: (anchor: Component) => void;
  shouldShowOverflowMenu: boolean;
}

// magic number based on height of overflow menu
// on web. This is used to determine if the menu
// should be displayed above or below the anchor.
// adjust if adding/removing menu options
const OVERFLOW_MENU_HEIGHT_WEB = 300;

interface Props {
  entity: Entity;
  onShare: () => void;
  onDownload: () => void;
  onPressOpenEntity: () => void;
  webHorizontalAlignment?: 'left' | 'right';
  workflowTemplate: MBAPI.WorkflowTemplate;
}

export function useOverflowMenu({
  entity,
  onShare,
  workflowTemplate,
  onDownload,
  onPressOpenEntity,
  webHorizontalAlignment = 'right',
}: Props): OverflowMenuHooks {
  // - TODO: this hook is somewhat coupled with entity selection, so need to
  //   refactor a bit
  const workflowTemplateId = workflowTemplate?.id;
  const duplicateEntities = useDuplicateEntities({ workflowTemplateId });
  const removeCardWithConfirmation = useDeleteCardWithConfirmation({
    workflowTemplateId,
    entityId: entity.id,
  });
  const { canCreateCard, canUpdateCard, canDeleteCard } = useContext(ChannelPermissionsContext);
  const entitySelection = useEntitySelection();
  const { navigate } = useNavigator();
  const channel = useContext(CardChannelContext);
  const channelSlug = channel?.slug;
  const isNoCodeWorkspace = !isNil(channel?.workflowTemplateId);

  const showOverflowMenu = useCallback(
    (anchor: Component) => {
      // eslint-disable-next-line react/no-find-dom-node
      const { y } = (findDOMNode(anchor) as Element).getBoundingClientRect();
      return MBActionSheet.show({
        parent: anchor,
        webVerticalAlignment: y + OVERFLOW_MENU_HEIGHT_WEB > window.innerHeight ? 'above' : 'below',
        webHorizontalAlignment,
        onClose: () => {},
        options: [
          {
            title: i18n('common.edit'),
            hidden: !canUpdateCard.enabled,
            onPress: onPressOpenEntity,
          },
          {
            title: isNoCodeWorkspace
              ? i18n('card.menu.actions.duplicateFrom')
              : i18n('card.menu.actions.duplicate'),
            hidden: !canCreateCard.enabled,
            onPress: () => {
              if (isNoCodeWorkspace) {
                navigate('CardCreate', {
                  channelSlug: channelSlug ?? '',
                  fromEntity: entity.id,
                });
              } else {
                const entityToCreate = toCreateWorkflowEntity(entity, workflowTemplate);
                duplicateEntities([entityToCreate]);
              }
            },
          },
          {
            title: i18n('common.share'),
            onPress: onShare,
          },
          {
            title: i18n('card.menu.actions.selectMultiple'),
            onPress: () => {
              entitySelection.addEntity(entity);
            },
          },
          {
            title: i18n('common.download'),
            hidden: isNil(onDownload),
            onPress: onDownload,
          },
          {
            title: i18n('card.menu.actions.cardHistory'),
            hidden: isNil(channelSlug),
            onPress: () => {
              navigate('CardHistory', { cardId: entity.id, channelSlug: channelSlug ?? '' });
            },
          },
          {
            title: i18n('common.delete'),
            destructive: true,
            hidden: !canDeleteCard.enabled,
            onPress: () => removeCardWithConfirmation(anchor),
          },
        ],
      });
    },
    [
      canUpdateCard.enabled,
      onPressOpenEntity,
      canCreateCard.enabled,
      onShare,
      onDownload,
      canDeleteCard.enabled,
      entity,
      removeCardWithConfirmation,
      webHorizontalAlignment,
      entitySelection,
      channelSlug,
      navigate,
      duplicateEntities,
      isNoCodeWorkspace,
      workflowTemplate,
    ],
  );

  return {
    shouldShowOverflowMenu: canCreateCard.enabled || canDeleteCard.enabled || canUpdateCard.enabled,
    showOverflowMenu: showOverflowMenu,
  };
}

function useDeleteCardWithConfirmation({
  workflowTemplateId,
  entityId,
}: {
  workflowTemplateId: string;
  entityId: MBAPI.Scalar.UUID;
}) {
  const deleteEntities = useDeleteEntitiesMutation(workflowTemplateId);
  const { show: showToast } = useToast();
  return useCallback(
    (anchor: Component) => {
      async function remove() {
        try {
          await deleteEntities([entityId]);
          showToast({
            icon: 'check-filled',
            message: i18n('card.details.deleteCompletedNoName'),
          });
        } catch (error) {
          console.error(error);
          showToast({
            style: 'warning',
            message: i18n('errors.somethingWentWrong'),
          });
        }
      }
      MBActionSheet.show({
        parent: anchor,
        title: i18n('card.details.deleteConfirmation.title'),
        webHorizontalAlignment: 'right',
        options: [
          {
            title: i18n('card.details.deleteConfirmation.confirm'),
            destructive: true,
            onPress: () => {
              remove();
            },
          },
          {
            title: i18n('common.cancel'),
          },
        ],
      });
    },
    [entityId, deleteEntities, showToast],
  );
}
